import React, { useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Badge, Popover } from 'antd';
import { list, reset } from 'actions';
import settings from 'settings';
import REDUCER_KEY from 'reducers/reducer.keys';
import filterValidations from './helper';
import Table from './Table';

const { NOTIFICATIONS_UPDATE_INTERVAL_MS } = settings;
/**
 * @description Create an interval hook to keep state in scope
 * @param {callback} Function
 * @param {delay} Number
 * @triggers [{useEffect}, {useRef}]
 */
const useInterval = (callback, delay) => {
  const savedCallback = useRef();

  // Remember the latest callback.
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  // Set up the interval.
  useEffect(() => {
    const tick = () => {
      savedCallback.current();
    };
    if (delay !== null) {
      const id = setInterval(tick, delay);
      return () => clearInterval(id);
    }

    return true;
  }, [delay]);
};

const mapStateToProps = state => ({
  validations: state.api.validationRosterTasks.data.results,
  inFlight: state.api.validationRosterTasks.data.inFlight
});

const mapDispatchToProps = (dispatch, ownProps) => ({
  resetValidationTasks() {
    dispatch(reset(REDUCER_KEY.validationRosterTasks.key));
  },
  loadValidationTasks() {
    const sorted = [
      {
        id: 'triggered_on',
        desc: true
      },
      {
        id: 'validation',
        desc: true
      }
    ];
    const filters = [
      {
        id: 'roster_version__id',
        value: ownProps.rosterId
      },
      {
        id: 'boundary_start',
        value: ownProps.start.format('YYYY-MM-DDTHH:mm:ssZ')
      },
      {
        id: 'boundary_end',
        value: ownProps.end.format('YYYY-MM-DDTHH:mm:ssZ')
      }
    ];
    dispatch(list(REDUCER_KEY.validationRosterTasks.key, {
      page: 0,
      pageSize: 20,
      filters,
      sorted
    }));
  }
});

const Counters = ({
  loadValidationTasks, resetValidationTasks, start, end, validations
}) => {
  // setup the interval hook
  useInterval(() => loadValidationTasks(), NOTIFICATIONS_UPDATE_INTERVAL_MS);
  // first load get the validations
  useEffect(() => {
    loadValidationTasks();
    return resetValidationTasks();
  }, []);
  // reload when start and end change
  useEffect(() => {
    loadValidationTasks();
  }, [start, end]);

  const filteredValidations = filterValidations(validations);

  const errors = filteredValidations.filter(item => item.status === 'Error' || item.status === 'Failed');
  const running = filteredValidations.filter(item => item.status === 'Running' || item.status === 'Queued');
  const passed = filteredValidations.filter(item => item.status === 'Passed');
  return (
    <>
      <span style={{ margin: '0 5px' }}>
        <Popover
          title="Errored or Failed"
          content={(
            <Table
              dataSource={errors}
              columns={[{
                title: '#Errors',
                dataIndex: 'validations_errors',
                key: 'validations_errors',
                render: (text, record) => (record.validation_errors.length)
              }]}
            />
          )}
          overlayClassName="bhp-fatigue-counter"
          placement="top"
        >
          <Badge
            count={errors.length}
            overflowCount={10}
            showZero
            title={`${errors.length} failed validations`}
          />
        </Popover>
      </span>
      <span style={{ margin: '0 5px' }}>
        <Badge
          count={running.length}
          overflowCount={10}
          showZero
          style={{ backgroundColor: '#2db7f5' }}
          title={`${running.length} running validations`}
        />
      </span>
      <span style={{ margin: '0 5px' }}>
        <Popover
          title="Passed"
          content={(
            <Table
              expandRowByClick={false}
              dataSource={passed}
            />
          )}
          overlayClassName=""
          placement="top"
        >
          <Badge
            count={passed.length}
            overflowCount={10}
            showZero
            style={{ backgroundColor: '#52c41a' }}
            title={`${passed.length} passed validations`}
          />
        </Popover>
      </span>
    </>
  );
};

Counters.propTypes = {
  validations: PropTypes.array,
  loadValidationTasks: PropTypes.func,
  resetValidationTasks: PropTypes.func,
  start: PropTypes.object.isRequired,
  end: PropTypes.object.isRequired
};

Counters.defaultProps = {
  validations: [],
  loadValidationTasks() {
  },
  resetValidationTasks() {
  }
};

export default connect(mapStateToProps, mapDispatchToProps)(React.memo(Counters));
